import React, { useState, useEffect } from 'react';
import { useToast } from '../../Toast';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const OnlinePayments: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  // Business details
  const [businessType, setBusinessType] = useState<string>('');
  const [country, setCountry] = useState<string>('United States');
  const [statementDescriptor, setStatementDescriptor] = useState<string>('');

  // Bank details
  const [accountHolderName, setAccountHolderName] = useState<string>('');
  const [routingNumber, setRoutingNumber] = useState<string>('');
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState<string>('');

  // Your details
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');

  // Identity verification
  const [idType, setIdType] = useState<'passport' | 'drivers_license' | 'identity_card'>('passport');
  const [idFrontFile, setIdFrontFile] = useState<File | null>(null);
  const [idBackFile, setIdBackFile] = useState<File | null>(null);
  const [idFrontPreview, setIdFrontPreview] = useState<string>('');
  const [idBackPreview, setIdBackPreview] = useState<string>('');

  // Terms acceptance
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

  // Show routing number tooltip
  const [showRoutingTooltip, setShowRoutingTooltip] = useState<boolean>(false);

  // Load saved settings from Firestore
  useEffect(() => {
    if (!user) return;
    const loadSettings = async () => {
      try {
        const docRef = doc(db, 'billing_settings', 'online_payments');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const settings = snap.data();
          setBusinessType(settings.businessType || '');
          setCountry(settings.country || 'United States');
          setStatementDescriptor(settings.statementDescriptor || '');
          setAccountHolderName(settings.accountHolderName || '');
          setRoutingNumber(settings.routingNumber || '');
          setFirstName(settings.firstName || '');
          setLastName(settings.lastName || '');
          setPhoneNumber(settings.phoneNumber || '');
          setEmail(settings.email || '');
          setIdType(settings.idType || 'passport');
        }
      } catch (err) {
        console.error('Error loading online payments settings:', err);
      }
    };
    loadSettings();
  }, [user]);

  const handleIdFrontUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file
      if (file.size > 5 * 1024 * 1024) {
        toast.warning('File size must be less than 5 MB');
        return;
      }
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        toast.warning('File type must be JPG, JPEG, or PNG');
        return;
      }

      setIdFrontFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setIdFrontPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIdBackUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file
      if (file.size > 5 * 1024 * 1024) {
        toast.warning('File size must be less than 5 MB');
        return;
      }
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        toast.warning('File type must be JPG, JPEG, or PNG');
        return;
      }

      setIdBackFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setIdBackPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (!businessType) {
      toast.warning('Please select a business type');
      return;
    }
    if (!statementDescriptor.trim()) {
      toast.warning('Please enter a statement descriptor');
      return;
    }
    if (!accountHolderName.trim()) {
      toast.warning('Please enter account holder name');
      return;
    }
    if (!routingNumber.trim() || routingNumber.length !== 9) {
      toast.warning('Please enter a valid 9-digit routing number');
      return;
    }
    if (!accountNumber.trim()) {
      toast.warning('Please enter account number');
      return;
    }
    if (accountNumber !== confirmAccountNumber) {
      toast.warning('Account numbers do not match');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast.warning('Please enter your first and last name');
      return;
    }
    if (!phoneNumber.trim()) {
      toast.warning('Please enter your phone number');
      return;
    }
    if (!email.trim()) {
      toast.warning('Please enter your email');
      return;
    }
    if (!idFrontFile) {
      toast.warning('Please upload your ID');
      return;
    }
    if (idType === 'drivers_license' && !idBackFile) {
      toast.warning('Please upload the back of your driver\'s license');
      return;
    }
    if (!termsAccepted) {
      toast.warning('Please accept the Stripe Terms and Conditions');
      return;
    }

    const settings = {
      businessType,
      country,
      statementDescriptor,
      accountHolderName,
      routingNumber,
      // Don't save full account number for security
      accountNumberLastFour: accountNumber.slice(-4),
      firstName,
      lastName,
      phoneNumber,
      email,
      idType,
      idUploaded: true,
      termsAccepted,
      ownerId: user?.uid || '',
      setupDate: new Date().toISOString()
    };

    try {
      const docRef = doc(db, 'billing_settings', 'online_payments');
      await setDoc(docRef, settings, { merge: true });
      toast.success('Online payment settings saved successfully! Your application will be reviewed by Stripe.');
    } catch (err) {
      console.error('Error saving online payments settings:', err);
      toast.error('Failed to save online payment settings.');
    }
  };

  return (
    <div style={{
      padding: '24px',
      backgroundColor: '#0A2540',
      minHeight: '100vh',
      color: '#FFFFFF',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '600',
          marginBottom: '8px',
          color: '#FFFFFF'
        }}>
          Online payments
        </h1>
        <p style={{
          fontSize: '14px',
          color: '#87CEEB',
          marginBottom: '0'
        }}>
          Set up and manage online payments
        </p>
      </div>

      {/* Info Banner */}
      <div style={{
        backgroundColor: '#E8F4F8',
        padding: '24px',
        borderRadius: '8px',
        marginBottom: '32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '12px',
            color: '#0A2540'
          }}>
            Receive payments from clients today
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#0A2540',
            marginBottom: '8px'
          }}>
            Add your bank details; that's it. No card readers or terminals, and no setup.
          </p>
          <p style={{
            fontSize: '14px',
            color: '#0A2540',
            marginBottom: '0'
          }}>
            Get paid faster.
          </p>

          <div style={{ marginTop: '20px', display: 'flex', gap: '40px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{
                  color: '#10A37F',
                  fontSize: '18px',
                  marginRight: '8px'
                }}>✓</span>
                <strong style={{ color: '#0A2540', fontSize: '14px' }}>
                  All major credit & debit cards accepted
                </strong>
              </div>
              <p style={{
                fontSize: '13px',
                color: '#0A2540',
                marginLeft: '26px',
                marginTop: '4px'
              }}>
                This also includes FSA and HSA cards
              </p>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{
                  color: '#10A37F',
                  fontSize: '18px',
                  marginRight: '8px'
                }}>✓</span>
                <strong style={{ color: '#0A2540', fontSize: '14px' }}>
                  Transparent processing fees
                </strong>
              </div>
              <p style={{
                fontSize: '13px',
                color: '#0A2540',
                marginLeft: '26px',
                marginTop: '4px'
              }}>
                For each successful charge: 3.15% + 30¢ and no fees for refunds
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Business details */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '20px',
          color: '#FFFFFF'
        }}>
          Business details
        </h2>

        <div style={{ maxWidth: '600px' }}>
          {/* Business type */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              color: '#FFFFFF'
            }}>
              Business type
            </label>
            <select
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: '#113F67',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">Select</option>
              <option value="sole_proprietor">Sole Proprietor/Individual</option>
              <option value="corporation">Corporation</option>
              <option value="llc">LLC</option>
              <option value="non_profit">Non-Profit</option>
            </select>
          </div>

          {/* Country */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              color: '#FFFFFF'
            }}>
              Country
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: '#113F67',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="United States">United States</option>
              <option value="Canada">Canada</option>
            </select>
          </div>

          {/* Statement descriptor */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              marginBottom: '8px',
              fontSize: '14px',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center'
            }}>
              Statement descriptor
              <span style={{
                marginLeft: '6px',
                fontSize: '12px',
                color: '#87CEEB',
                cursor: 'help',
                border: '1px solid #87CEEB',
                borderRadius: '50%',
                width: '16px',
                height: '16px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>?</span>
            </label>
            <input
              type="text"
              value={statementDescriptor}
              onChange={(e) => setStatementDescriptor(e.target.value)}
              placeholder="The Real Therapists of"
              maxLength={22}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: '#113F67',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px'
              }}
            />
            <p style={{
              fontSize: '12px',
              color: '#87CEEB',
              marginTop: '6px'
            }}>
              Use the name for your business that your customers will recognize to help prevent unintended chargebacks.
            </p>
          </div>
        </div>
      </div>

      {/* Bank details */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '12px',
          color: '#FFFFFF'
        }}>
          Bank details
        </h2>
        <p style={{
          fontSize: '14px',
          color: '#87CEEB',
          marginBottom: '12px'
        }}>
          Add details for a bank account that supports withdrawals, like a checking account.
        </p>
        <p style={{
          fontSize: '13px',
          color: '#87CEEB',
          marginBottom: '20px'
        }}>
          If your business is a sole proprietor, partnership, or single member LLC, the account holder name must be the business's DBA or that of a business representative. If your business is an LLC, corporation, or non-profit, the account holder name must be the legal business name or DBA.
        </p>

        <div style={{ maxWidth: '600px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            {/* Account holder name */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#FFFFFF'
              }}>
                Account holder name
              </label>
              <input
                type="text"
                value={accountHolderName}
                onChange={(e) => setAccountHolderName(e.target.value)}
                placeholder="Jamie Appleseed"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#113F67',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Routing number */}
            <div style={{ position: 'relative' }}>
              <label style={{
                marginBottom: '8px',
                fontSize: '14px',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center'
              }}>
                Routing number
                <span
                  onMouseEnter={() => setShowRoutingTooltip(true)}
                  onMouseLeave={() => setShowRoutingTooltip(false)}
                  style={{
                    marginLeft: '6px',
                    fontSize: '12px',
                    color: '#87CEEB',
                    cursor: 'help',
                    border: '1px solid #87CEEB',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >?</span>
              </label>
              <input
                type="text"
                value={routingNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 9) setRoutingNumber(value);
                }}
                placeholder="111000000"
                maxLength={9}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#113F67',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
              {showRoutingTooltip && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  width: '300px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  zIndex: 1000
                }}>
                  <p style={{ marginBottom: '8px', fontWeight: '600' }}>
                    The routing number (9 digits) is normally found on a check provided by your bank.
                  </p>
                  <div style={{
                    backgroundColor: '#E8F4F8',
                    padding: '8px',
                    borderRadius: '4px',
                    color: '#0A2540',
                    fontSize: '11px'
                  }}>
                    <div style={{ marginBottom: '4px' }}>BANK</div>
                    <div style={{ fontSize: '10px' }}>JAMIE APPLESEED, 123 MAIN ST</div>
                    <div style={{ fontSize: '10px' }}>SANTA MONICA, CA</div>
                    <div style={{ marginTop: '8px', color: '#E57373', fontWeight: '600' }}>
                      123456789
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            {/* Account number */}
            <div>
              <label style={{
                marginBottom: '8px',
                fontSize: '14px',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center'
              }}>
                Account number
                <span style={{
                  marginLeft: '6px',
                  fontSize: '12px',
                  color: '#87CEEB',
                  cursor: 'help',
                  border: '1px solid #87CEEB',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>?</span>
              </label>
              <input
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#113F67',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Confirm account number */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#FFFFFF'
              }}>
                Confirm account number
              </label>
              <input
                type="text"
                value={confirmAccountNumber}
                onChange={(e) => setConfirmAccountNumber(e.target.value.replace(/\D/g, ''))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#113F67',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Your details */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '12px',
          color: '#FFFFFF'
        }}>
          Your details
        </h2>
        <p style={{
          fontSize: '14px',
          color: '#87CEEB',
          marginBottom: '20px'
        }}>
          Please provide details for the person who fills out this application.
        </p>

        <div style={{ maxWidth: '600px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            {/* First Name */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#FFFFFF'
              }}>
                First Name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#113F67',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Last Name */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#FFFFFF'
              }}>
                Last Name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#113F67',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            {/* Phone number */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#FFFFFF'
              }}>
                Phone number
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Phone number"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#113F67',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Email */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#FFFFFF'
              }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@mail.com"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#113F67',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Your identity verification */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '12px',
          color: '#FFFFFF'
        }}>
          Your identity verification
        </h2>
        <p style={{
          fontSize: '14px',
          color: '#87CEEB',
          marginBottom: '20px'
        }}>
          This verification is used to confirm your identity and protect you from identity theft.
          <br />
          Please upload a copy of a valid government-issued ID.
        </p>

        {/* Requirements box */}
        <div style={{
          backgroundColor: '#113F67',
          border: '1px solid #6CA6C1',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '20px',
          maxWidth: '600px'
        }}>
          <ul style={{
            margin: '0',
            paddingLeft: '20px',
            fontSize: '13px',
            color: '#87CEEB'
          }}>
            <li>Copy of identification must be in color</li>
            <li>Image must be in good quality and legible</li>
            <li>File size limit is 5 MB</li>
            <li>File types supported: JPG, JPEG and PNG</li>
          </ul>
        </div>

        <div style={{ maxWidth: '600px' }}>
          {/* Select ID type */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '12px',
              fontSize: '14px',
              color: '#FFFFFF'
            }}>
              Select ID type
            </label>
            <div style={{ display: 'flex', gap: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="passport"
                  checked={idType === 'passport'}
                  onChange={(e) => setIdType(e.target.value as any)}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Passport</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="drivers_license"
                  checked={idType === 'drivers_license'}
                  onChange={(e) => setIdType(e.target.value as any)}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Driver's license</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="identity_card"
                  checked={idType === 'identity_card'}
                  onChange={(e) => setIdType(e.target.value as any)}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Identity card</span>
              </label>
            </div>
          </div>

          {/* Upload file */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '12px',
              fontSize: '14px',
              color: '#FFFFFF'
            }}>
              Upload file
            </label>

            {/* Front upload */}
            <div style={{
              border: '2px dashed #6CA6C1',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              marginBottom: idType === 'drivers_license' ? '16px' : '0',
              backgroundColor: '#113F67',
              cursor: 'pointer',
              position: 'relative'
            }}>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleIdFrontUpload}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer'
                }}
              />
              {idFrontPreview ? (
                <div>
                  <img
                    src={idFrontPreview}
                    alt="ID preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '150px',
                      borderRadius: '4px'
                    }}
                  />
                  <p style={{ marginTop: '12px', fontSize: '13px', color: '#87CEEB' }}>
                    Click to change
                  </p>
                </div>
              ) : (
                <div>
                  <div style={{
                    fontSize: '40px',
                    color: '#6CA6C1',
                    marginBottom: '12px'
                  }}>☁</div>
                  <p style={{
                    fontSize: '14px',
                    color: '#FFFFFF',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    Upload {idType === 'passport' ? 'image of passport' :
                            idType === 'drivers_license' ? 'front of driver\'s license' :
                            'image of ID card'}
                  </p>
                  <p style={{ fontSize: '13px', color: '#87CEEB' }}>
                    Drag or <span style={{ color: '#4A9EFF' }}>browse</span> for a file to upload
                  </p>
                </div>
              )}
            </div>

            {/* Back upload for driver's license */}
            {idType === 'drivers_license' && (
              <div style={{
                border: '2px dashed #6CA6C1',
                borderRadius: '8px',
                padding: '40px',
                textAlign: 'center',
                backgroundColor: '#113F67',
                cursor: 'pointer',
                position: 'relative'
              }}>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleIdBackUpload}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                />
                {idBackPreview ? (
                  <div>
                    <img
                      src={idBackPreview}
                      alt="ID back preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '150px',
                        borderRadius: '4px'
                      }}
                    />
                    <p style={{ marginTop: '12px', fontSize: '13px', color: '#87CEEB' }}>
                      Click to change
                    </p>
                  </div>
                ) : (
                  <div>
                    <div style={{
                      fontSize: '40px',
                      color: '#6CA6C1',
                      marginBottom: '12px'
                    }}>☁</div>
                    <p style={{
                      fontSize: '14px',
                      color: '#FFFFFF',
                      fontWeight: '600',
                      marginBottom: '8px'
                    }}>
                      Upload back of driver's license
                    </p>
                    <p style={{ fontSize: '13px', color: '#87CEEB' }}>
                      Drag or <span style={{ color: '#4A9EFF' }}>browse</span> for a file to upload
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Terms and conditions */}
      <div style={{ marginBottom: '32px', maxWidth: '600px' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            style={{ marginRight: '8px', marginTop: '3px' }}
          />
          <span style={{ fontSize: '14px', color: '#FFFFFF' }}>
            I have read and accept the Stripe{' '}
            <a
              href="https://stripe.com/legal/connect-account"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#4A9EFF', textDecoration: 'none' }}
            >
              Terms and Conditions
            </a>
          </span>
        </label>
      </div>

      {/* Save button */}
      <div>
        <button
          onClick={handleSave}
          style={{
            padding: '12px 32px',
            backgroundColor: '#4A9EFF',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#3A8EEF'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#4A9EFF'}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default OnlinePayments;
